import {
  GENERIC_ERROR,
  messageFor,
  toFormErrors,
  withoutErrorsFor,
  type FormErrors,
} from '../../lib/formErrors'

/**
 * The field maps for the four forms this feature owns. One code belongs to exactly
 * one input on its form; the shared codes (phone_invalid, mc_number_invalid,
 * address_*) keep that rule inside each form, which is where it matters.
 */

const CARRIER_FIELD_BY_CODE: Record<string, string> = {
  carrier_name_required: 'name',
  carrier_name_invalid: 'name',
  carrier_name_too_long: 'name',
  mc_number_invalid: 'mcNumber',
  carrier_duplicate_mc_number: 'mcNumber',
  usdot_number_invalid: 'usdotNumber',
  carrier_duplicate_usdot_number: 'usdotNumber',
  carrier_tax_id_too_long: 'taxId',
  commission_rate_out_of_range: 'commissionRate',
  address_street_required: 'line1',
  address_street_too_long: 'line1',
  address_unit_too_long: 'line2',
  address_city_required: 'city',
  address_city_too_long: 'city',
  address_state_invalid: 'state',
  address_zip_invalid: 'postalCode',
  address_zip_state_mismatch: 'postalCode',
  phone_invalid: 'phone',
  carrier_email_invalid: 'email',
}

const CARRIER_CLEARED_BY: Record<string, readonly string[]> = {
  address_zip_state_mismatch: ['state', 'postalCode'],
}

const DRIVER_FIELD_BY_CODE: Record<string, string> = {
  driver_first_name_required: 'firstName',
  driver_first_name_invalid: 'firstName',
  driver_first_name_too_long: 'firstName',
  driver_last_name_required: 'lastName',
  driver_last_name_invalid: 'lastName',
  driver_last_name_too_long: 'lastName',
  phone_invalid: 'phone',
  driver_email_invalid: 'email',
  driver_cdl_too_long: 'cdlNumber',
  driver_cdl_invalid: 'cdlNumber',
  // The license is the pair (TRKC-97): the number another driver holds is said on the
  // number's box, and every complaint about the state on the state's.
  driver_duplicate_cdl: 'cdlNumber',
  driver_cdl_state_required: 'cdlState',
  driver_cdl_state_invalid: 'cdlState',
  driver_cdl_state_without_number: 'cdlState',
}

/**
 * Complaints one half of the license settles from the other box: a number typed or
 * cleared answers "the state needs a number" and "the number needs a state", and another
 * state makes another license, so it answers "another driver holds this one".
 */
const DRIVER_CLEARED_BY: Record<string, readonly string[]> = {
  driver_cdl_state_required: ['cdlNumber'],
  driver_cdl_state_without_number: ['cdlNumber'],
  driver_duplicate_cdl: ['cdlState'],
}

const TRUCK_FIELD_BY_CODE: Record<string, string> = {
  truck_unit_required: 'unitNumber',
  truck_unit_invalid: 'unitNumber',
  truck_unit_too_long: 'unitNumber',
  // Another active truck of the carrier holds the number (TRKC-105); the subject is that truck.
  truck_duplicate_unit: 'unitNumber',
  truck_type_invalid: 'type',
  truck_make_too_long: 'make',
  truck_model_too_long: 'model',
  truck_vin_invalid: 'vin',
  truck_plate_invalid: 'licensePlate',
  truck_plate_too_long: 'licensePlate',
  // A straight box truck's own room and features (TRKC-100).
  truck_pallets_not_box_truck: 'palletCapacity',
  truck_weight_not_box_truck: 'weightCapacity',
  truck_features_not_box_truck: 'features',
  truck_features_invalid: 'features',
  truck_feature_not_for_type: 'features',
  // A posting not withdrawn holds the truck's kind; the subject is that posting (Copilot on #48).
  truck_type_posted: 'type',
  // A straight box truck's box (TRKC-102).
  truck_box_length_required: 'boxLength',
  truck_box_length_invalid: 'boxLength',
  truck_box_length_not_box_truck: 'boxLength',
  pallet_capacity_out_of_range: 'palletCapacity',
  weight_out_of_range: 'weightCapacity',
}

const TRAILER_FIELD_BY_CODE: Record<string, string> = {
  trailer_number_required: 'trailerNumber',
  trailer_number_invalid: 'trailerNumber',
  trailer_number_too_long: 'trailerNumber',
  // Another active trailer of the carrier holds the number (TRKC-105); the subject is that trailer.
  trailer_duplicate_number: 'trailerNumber',
  trailer_type_invalid: 'type',
  trailer_type_straight_box: 'type',
  trailer_features_invalid: 'features',
  trailer_feature_not_for_type: 'features',
  trailer_vin_invalid: 'vin',
  pallet_capacity_out_of_range: 'palletCapacity',
  weight_out_of_range: 'weightCapacity',
}

export { GENERIC_ERROR, messageFor }
export type { FormErrors }

export const toCarrierFormErrors = (caught: unknown): FormErrors =>
  toFormErrors(caught, CARRIER_FIELD_BY_CODE)
export const withoutCarrierErrorsFor = (errors: FormErrors, edited: string): FormErrors =>
  withoutErrorsFor(errors, edited, CARRIER_CLEARED_BY)

export const toDriverFormErrors = (caught: unknown): FormErrors =>
  toFormErrors(caught, DRIVER_FIELD_BY_CODE)
export const withoutDriverErrorsFor = (errors: FormErrors, edited: string): FormErrors =>
  withoutErrorsFor(errors, edited, DRIVER_CLEARED_BY)

export const toTruckFormErrors = (caught: unknown): FormErrors =>
  toFormErrors(caught, TRUCK_FIELD_BY_CODE)
export const withoutTruckErrorsFor = withoutErrorsFor

export const toTrailerFormErrors = (caught: unknown): FormErrors =>
  toFormErrors(caught, TRAILER_FIELD_BY_CODE)
export const withoutTrailerErrorsFor = withoutErrorsFor
